
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ExamQuestion } from '@/hooks/useExamQuestions';

interface ExamQuestionFiltersProps {
  filters: {
    module: string;
    part: string;
    language: string;
    isActive: string;
  };
  onFiltersChange: (filters: any) => void;
  questions: ExamQuestion[];
}

export function ExamQuestionFilters({ filters, onFiltersChange, questions }: ExamQuestionFiltersProps) {
  const uniqueModules = [...new Set(questions.map(q => q.module_number))].sort((a, b) => a - b);
  const uniqueParts = [...new Set(questions.map(q => q.part_number))].sort((a, b) => a - b);
  const uniqueLanguages = [...new Set(questions.map(q => q.language))];

  // Calculate filtered count for display
  const filteredCount = questions.filter(question => {
    if (filters.module && question.module_number.toString() !== filters.module) return false;
    if (filters.part && question.part_number.toString() !== filters.part) return false;
    if (filters.language && question.language !== filters.language) return false;
    if (filters.isActive !== 'all') {
      const isActive = filters.isActive === 'true';
      if (question.is_active !== isActive) return false;
    }
    return true;
  }).length;

  // Calculate total count for the selected language only
  const languageTotalCount = questions.filter(question => 
    question.language === filters.language
  ).length;

  return (
    <div className="flex flex-wrap gap-4 items-center">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Module:</span>
        <Select
          value={filters.module || "all"}
          onValueChange={(value) => onFiltersChange({ ...filters, module: value === "all" ? "" : value })}
        >
          <SelectTrigger className="w-24">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {uniqueModules.map(module => (
              <SelectItem key={module} value={module.toString()}>
                {module}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Part:</span>
        <Select
          value={filters.part || "all"}
          onValueChange={(value) => onFiltersChange({ ...filters, part: value === "all" ? "" : value })}
        >
          <SelectTrigger className="w-24">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {uniqueParts.map(part => (
              <SelectItem key={part} value={part.toString()}>
                {part}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Language:</span>
        <Select
          value={filters.language}
          onValueChange={(value) => onFiltersChange({ ...filters, language: value })}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {uniqueLanguages.map(language => (
              <SelectItem key={language} value={language}>
                {language}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Status:</span>
        <Select
          value={filters.isActive}
          onValueChange={(value) => onFiltersChange({ ...filters, isActive: value })}
        >
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing {filteredCount} of {languageTotalCount} {filters.language} questions
      </div>
    </div>
  );
}
